import React from 'react';
import { BranchesOutlined, CheckOutlined, ClockCircleOutlined, CloseOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Card, Upload, Empty, UploadFile } from 'antd';
import classes from './RoomAccess.module.scss';
import { ItemRender, RcFile } from 'antd/es/upload/interface';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import { useTranslation } from 'react-i18next';
import api from 'api';
import axios from 'axios';
import cn from 'classnames';
import { ConfirmationModal } from 'components/Modals';
import ModalNDA from './ModalNDA/ModalNDA';
import ModalNDAVersions from './ModalNDAVersions/ModalNDAVersions';
import Assets from 'assets/icons';

const { Dragger } = Upload;

export type NDAVersion = {
  id: string;
  key: string;
  name: string;
  date: string;
  author: string;
  version_comment: string;
  is_current: boolean;
};

const RoomAccess = () => {
  const [isUpdateLoading, setIsUpdateLoading] = React.useState<boolean>(false);
  const [NDADocument, setNDADocument] = React.useState<UploadFile[] | undefined>(undefined);
  const [confirmUploadingModal, setConfirmUploadingModal] = React.useState(false);
  const [confirmDeleteFileModal, setConfirmDeleteFileModal] = React.useState(false);
  const [isVersionsModalOpen, setIsVersionsModalOpen] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [versionsData, setVersionsData] = React.useState<NDAVersion[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = React.useState(false);

  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { messageApi } = useAppSelector((state) => state.windowState);
  const { userPermissions } = useAppSelector((state) => state.userData);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const fetchVersions = React.useCallback(async () => {
    if (!dataRoom?.id) return;
    setIsLoadingVersions(true);
    try {
      const response = await api.getNDAVersions({ room_id: dataRoom.id });
      const mapped: NDAVersion[] = (response.data || []).map((version: any, idx: number) => ({
        id: version.id,
        key: version.id ?? String(idx),
        name: version.name || version.agreement_file || '',
        date: version.upload_at || version.created_at || '-',
        author: version.author?.name || '-',
        version_comment: version.version_comment || '-',
        is_current: version.id === dataRoom?.agreement_id
      }));
      setVersionsData(mapped);
    } catch {
    } finally {
      setIsLoadingVersions(false);
    }
  }, [dataRoom?.id, dataRoom?.agreement_id]);

  React.useEffect(() => {
    if (dataRoom?.id) {
      fetchVersions();
    }
  }, [dataRoom?.id, dataRoom?.agreement_id, fetchVersions]);

  const canEdit = userPermissions?.can_edit_room_settings;
  const formatNDA = dataRoom?.agreement_file?.split('.')?.reverse()[0];  
  const icon = availableIconFormat.includes(formatNDA) ? formatNDA : 'default';

  React.useEffect(() => {
    setNDADocument(dataRoom?.agreement_enable
      ? [{ name: dataRoom?.agreement_file, uid: dataRoom?.agreement_id, type: 'document' }]
      : undefined
    )
  }, [dataRoom]);

  const onBeforeUpload = (file: UploadFile) => {    
    const fileExtension = file.name.split('.').reverse()[0];

    if (file.name.toLowerCase().replace(`.${fileExtension}`, '').length > 150) {
      messageApi?.error(t('Documents.error.fileNameTooLong'));
      return false;
    };

    if (['pdf', 'docx', 'doc'].includes(fileExtension)) {
      setNDADocument([file]);
    };
    return false;
  };

  const onRemove = () => setNDADocument([]);
  const itemRender: ItemRender = React.useMemo(
    () =>
      (originNode, doc, fileList, { remove }) => {
        const fileExtension = doc?.name?.split('.')?.reverse()[0];                
        if (fileExtension && ['pdf', 'docx', 'doc'].includes(fileExtension)) {
          return <div className={classes.file}>
            {originNode} <Button type='link' icon={<Assets.TrashIcon />} className={classes.deleteIcon} onClick={remove} />
          </div>
        }
      },
    []
  );

  const getStatusRow = (status?: number) => {
    switch(status) {
      case 3:
        return <div className={classes.statusTermWrap}>
          <CloseOutlined className={classes.rejectIcon}/>
          <span className={classes.reject}>{t('Settings.terms.state.rejected')}</span>
        </div>;
      case 2:
        return <div className={classes.statusTermWrap}>
          <CheckOutlined className={classes.acceptIcon}/>
          <span className={classes.accept}>{t('Settings.terms.state.accepted')}</span>
        </div>;
      default: 
        return <div className={classes.statusTermWrap}>
          <ClockCircleOutlined className={classes.notAcceptIcon}/>
          <span className={classes.nonAccept}>{t('Settings.terms.state.nonAccepted')}</span>
        </div>;
    };
  };

  const uploadedNDA = (
    <div className={cn(classes.uploadedWrap, canEdit ? classes.canEdit : classes.cantEdit)}>
      <div onClick={() => setIsModalOpen!(true)} className={classes.cardNDA}>
        <div className={classes.cardNameWrap}>
          <div className={classes.cardIcon}>{documnentIcons[icon as keyof typeof documnentIcons]}</div>
          <div className={classes.docCardName}>{NDADocument && NDADocument[0]?.name}</div>
        </div>
        <div className={classes.cardView}><EyeOutlined /></div>
      </div>
      {getStatusRow(dataRoom?.user_status)}

      {canEdit && (
        <div className={classes.ndaActions}>
          <Button
            type='text'
            onClick={() => setIsVersionsModalOpen(true)}
            icon={<BranchesOutlined  className={classes.historyIcon} />}
          />
          <Button
            danger
            type='text'
            loading={isUpdateLoading}
            onClick={() => setConfirmDeleteFileModal(true)}
            icon={<Assets.TrashIcon className={classes.deleteFileIcon} />}
          />
        </div>
      )}
    </div>
  );

  const iconRender = (file: UploadFile) => {
    const formatNDA = file?.name?.split('.')?.reverse()[0];  
    const icon = availableIconFormat.includes(formatNDA) ? formatNDA : 'default';
    return documnentIcons[icon as keyof typeof documnentIcons];
  };
  
  const onSave = async () => {
    if (NDADocument && !NDADocument[0]) {
      messageApi?.warning(t('Settings.error.fileUpload'));
      return;
    }
    setConfirmUploadingModal(false);
    setIsUpdateLoading(true);
    try {
      const formDataAgreement = new FormData() as any;
      formDataAgreement.append('room_id', dataRoom?.id);
      formDataAgreement.append('agreement_enable', String(true));
      NDADocument && formDataAgreement.append('agreement_file', NDADocument[0] as RcFile);
      await api.setRoomAgreement(formDataAgreement);
      setNDADocument([]);
      messageApi?.success(
        `${t('Settings.success.settingsUploaded.before')} ${dataRoom?.name}
        ${t('Settings.success.settingsUploaded.after')}`
      );
    } catch (err) {
      messageApi?.error(
        `${t('Settings.error.settingsUpload.before')} ${dataRoom?.name} 
        ${t('Settings.error.settingsUpload.after')}`
      );
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const onDeleteFile = async () => {
    if (!dataRoom?.id || !canEdit) return;
    setConfirmDeleteFileModal(false);
    setIsUpdateLoading(true);
    try {
      await api.deleteRoomAgreementFile({ room_id: dataRoom.id });
      dispatch(setDataRoom({ ...dataRoom, agreement_enable: false, agreement_file: null, agreement_id: null }));
      messageApi?.success(t('Settings.terms.deleteFile.success'));
    } catch (err) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : null;
      let errorMessage = detail || t('Settings.terms.deleteFile.error');

      if (typeof detail === 'string' && detail.includes('Unable to delete the NDA')) {
            
        errorMessage = t('Settings.terms.deleteFile.confidentialFilesError');
      }

      messageApi?.error(errorMessage);
    } finally {
      setIsUpdateLoading(false);
    }
  };
    
  return (
    <Card className={classes.card} title={t('Settings.terms.title')} variant='borderless'>
      {
        dataRoom?.agreement_enable
          ? uploadedNDA
          : <Dragger
            name='file'
            listType='picture'
            multiple={false}
            className={classes.dragger}
            disabled={NDADocument && !!NDADocument[0]}
            fileList={NDADocument}
            beforeUpload={onBeforeUpload}
            onRemove={onRemove}
            iconRender={iconRender}
            itemRender={itemRender}
            accept='.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf'
          >
            <Empty rootClassName={classes.emptyElement} image={Empty.PRESENTED_IMAGE_SIMPLE} description=''/>
            <p className='ant-upload-text'>{t('Settings.terms.uploadFile.select')}</p>
            <p className='ant-upload-hint'>{t('Settings.terms.uploadFile.subtitle')}</p>
          </Dragger>
      }

      {NDADocument && NDADocument[0] && !dataRoom?.agreement_file && <Button
        loading={isUpdateLoading}
        type='primary'
        onClick={() => setConfirmUploadingModal(true)}
      >
        {t('main.saveButton')}
      </Button>}
      
      {confirmUploadingModal && (
        <React.Suspense fallback={<div />}>
          <ConfirmationModal
            isOpen={confirmUploadingModal}
            onCancel={() => setConfirmUploadingModal(false)}
            isLoading={isUpdateLoading}
            submit={onSave}
            okText={t('Settings.terms.confirmModal.okButton')}
            text={<div className={classes.textModal}>{t('Settings.terms.confirmModal.textNDA')}</div>}
            title={<div className={classes.titleRow}>
              <ExclamationCircleOutlined className={classes.exclamationIcon}/>{t('Settings.terms.confirmModal.title')}
            </div>}
          />
        </React.Suspense>
      )}

      {confirmDeleteFileModal && (
        <React.Suspense fallback={<div />}>
          <ConfirmationModal
            isOpen={confirmDeleteFileModal}
            onCancel={() => setConfirmDeleteFileModal(false)}
            isLoading={isUpdateLoading}
            submit={onDeleteFile}
            okText={t('Settings.terms.deleteFile.confirmTitle')}
            text={<div className={classes.textModal}>{t('Settings.terms.deleteFile.confirmText', { fileName: dataRoom?.agreement_file || '' })}</div>}
            title={<div className={classes.titleRow}>
              <ExclamationCircleOutlined className={classes.exclamationIcon}/>{t('Settings.terms.deleteFile.confirmTitle')}
            </div>}
          />
        </React.Suspense>
      )}

      {isModalOpen && (
        <React.Suspense fallback={<div />}>
          <ModalNDA
            open={isModalOpen}
            setIsOpen={setIsModalOpen}
          />
        </React.Suspense>
      )}

      {isVersionsModalOpen && (
        <ModalNDAVersions
          open={isVersionsModalOpen}
          onClose={() => setIsVersionsModalOpen(false)}
          versionsData={versionsData}
          isLoadingVersions={isLoadingVersions}
          fetchVersions={fetchVersions}
        />
      )}
    </Card>
  );
};

export default RoomAccess;
